import * as moment from 'moment';
import { Transaction } from '../abstract/transaction.model';
import { BankAccountLineAllocation } from './bank-account-line-allocation.model';
import { BankAccountLineQualificationSource } from './bank-account-line-qualification-source.model';
import { BankAccountLineType } from '../config/transaction-type.model';
import { BankAccountRule } from '../rules/bank-account-rule.model';
import { PaymentProvider } from '../cards/payment-provider.model';
import { Cheque } from '../cheques/cheque.model';
import { Employee } from '../../human-resources/employee.model';
import { Loan } from '../loans/loan.model';
import { LoanInstalment } from '../loans/loan-instalment.model';
import { Partner } from '../../partner/partner.model';
import { PaymentMethod } from '../../core/payment-method.model';
import { Shareholder } from '../../client/shareholder.model';
import { BankAccountLineQualificationSourceEnum } from 'src/app/@shared/enums/bank-account-line-qualification-source.enum';
import { StatusEnum } from 'src/app/@shared/enums/status.enum';
import { BankAccount } from '../bank-accounts/bank-account.model';
import { CreditCard } from '../credit-cards/credit-card.model';

export class BankAccountLine extends Transaction {

    static override CLASS_NAME: string = 'BankAccountLine';

    private _aiEmployee!: Employee;
    private _aiLoan!: Loan;
    private _aiPartner!: Partner;
    private _aiPaymentMethod!: PaymentMethod;
    private _aiPaymentProvider!: PaymentProvider;
    private _aiShareholder!: Shareholder;
    private _aiType!: BankAccountLineType;
    private _amountSigned!: number;
    private _appliedRule!: BankAccountRule;
    private _bankAccount!: BankAccount;
    private _bankAccountId!: number;
    private _cheque!: Cheque;
    private _coming!: boolean;
    private _comment!: string;
    private _createdRule!: BankAccountRule;
    private _credit!: number;
    private _creditCard!: CreditCard;
    private _debit!: number;
    private _duplicate!: boolean;
    private _employee!: Employee;
    private _hasAutoSettlement!: boolean;
    private _hasCreatedRule!: boolean;
    private _hasSettlement!: boolean;
    private _justified!: boolean;
    private _lineHash!: string;
    private _loan!: Loan;
    private _loanInstalment!: LoanInstalment;
    private _loanInstalmentId!: number;
    private _memoryObservations!: number | null;
    private _memoryPartner!: Partner;
    private _memoryType!: BankAccountLineType;
    private _operationDetails!: string;
    private _operationNature!: string;
    private _origin!: string;
    private _paymentMethod!: PaymentMethod;
    private _paymentProvider!: PaymentProvider;
    private _qualificationSource!: BankAccountLineQualificationSource | null;
    private _valueDate!: moment.Moment;

    /*----------------------------------------------------------------------------------------
     * Méthodes statiques
     ----------------------------------------------------------------------------------------*/

    public static override statusSeverity(status: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch (true) {
            case (status === StatusEnum.Duplicate):
                return 'danger';
            default:
                return super.statusSeverity(status);
        }
    }

    public static override statusLabel(status: string): string {
        switch (true) {
            case status === StatusEnum.Duplicate.toString():
                return 'Doublon';
            default:
                return super.statusLabel(status);
        }
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public getAmountClass(): string {
        if (this.amountSigned > 0) {
            return 'text-green-500';
        } else {
            return 'text-red-500';
        }
    }

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    public get aiEmployee(): Employee {
        return this._aiEmployee;
    }

    public set aiEmployee(value: any) {
        this._setValue('_aiEmployee', value, 'Employee');
    }

    public get aiLoan(): Loan {
        return this._aiLoan;
    }

    public set aiLoan(value: any) {
        this._setValue('_aiLoan', value, 'Loan');
    }

    public get aiPartner(): Partner {
        return this._aiPartner;
    }

    public set aiPartner(value: any) {
        this._setValue('_aiPartner', value, 'Partner');
    }

    public get aiPaymentMethod(): PaymentMethod {
        return this._aiPaymentMethod;
    }

    public set aiPaymentMethod(value: any) {
        this._setValue('_aiPaymentMethod', value, 'PaymentMethod');
    }

    public get aiPaymentProvider(): PaymentProvider {
        return this._aiPaymentProvider;
    }

    public set aiPaymentProvider(value: any) {
        this._setValue('_aiPaymentProvider', value, 'PaymentProvider');
    }

    public get aiShareholder(): Shareholder {
        return this._aiShareholder;
    }

    public set aiShareholder(value: any) {
        this._setValue('_aiShareholder', value, 'Shareholder');
    }

    public get aiType(): BankAccountLineType {
        return this._aiType;
    }

    public set aiType(value: any) {
        this._setValue('_aiType', value, 'BankAccountLineType');
    }

    public override get allocations(): BankAccountLineAllocation[] {
        return this._allocations as BankAccountLineAllocation[];
    }

    public override set allocations(value: any) {
        this._setValue('_allocations', value, 'BankAccountLineAllocation');
    }

    public get amountSigned(): number {
        return this._amountSigned;
    }

    public set amountSigned(value: any) {
        this._setValue('_amountSigned', value, 'float');
    }

    public get appliedRule(): BankAccountRule {
        return this._appliedRule;
    }

    public set appliedRule(value: any) {
        this._setValue('_appliedRule', value, 'BankAccountRule');
    }

    public get bankAccount(): BankAccount {
        return this._bankAccount;
    }

    public set bankAccount(value: any) {
        this._setValue('_bankAccount', value, 'BankAccount');
    }

    public get bankAccountId(): number {
        return this._bankAccountId;
    }

    public set bankAccountId(value: any) {
        this._setValue('_bankAccountId', value, 'integer');
    }

    public get cheque(): Cheque {
        return this._cheque;
    }

    public set cheque(value: any) {
        this._setValue('_cheque', value, 'Cheque');
    }

    public get coming(): boolean {
        return this._coming;
    }

    public set coming(value: any) {
        this._setValue('_coming', value, 'boolean');
    }

    public get comment(): any {
        return this._comment;
    }

    public set comment(value: any) {
        this._setValue('_comment', value, 'string');
    }

    public get createdRule(): BankAccountRule {
        return this._createdRule;
    }

    public set createdRule(value: any) {
        this._setValue('_createdRule', value, 'BankAccountRule');
        if (value) {
            this.hasCreatedRule = true;
        }
    }

    public get credit(): number {
        return this._credit;
    }

    public set credit(value: any) {
        this._setValue('_credit', value, 'float');
    }

    public get creditCard(): CreditCard {
        return this._creditCard;
    }

    public set creditCard(value: any) {
        this._setValue('_creditCard', value, 'CreditCard');
    }

    public get debit(): number {
        return this._debit;
    }

    public set debit(value: any) {
        this._setValue('_debit', value, 'float');
    }

    public get duplicate(): boolean {
        return this._duplicate;
    }

    public set duplicate(value: any) {
        this._setValue('_duplicate', value, 'boolean');
    }

    public get employee(): Employee {
        return this._employee;
    }

    public set employee(value: any) {
        this._setValue('_employee', value, 'Loan');
    }

    public get hasAutoSettlement(): boolean {
        return this._hasAutoSettlement;
    }

    public set hasAutoSettlement(value: any) {
        this._setValue('_hasAutoSettlement', value, 'boolean');
    }

    public get hasCreatedRule(): boolean {
        return this._hasCreatedRule;
    }

    public set hasCreatedRule(value: any) {
        this._setValue('_hasCreatedRule', value, 'boolean');
    }

    public get hasSettlement(): boolean {
        return this._hasSettlement;
    }

    public set hasSettlement(value: any) {
        this._setValue('_hasSettlement', value, 'boolean');
    }

    public get isPartnerAiDetected(): boolean {
        return this._qualificationSource?.code === BankAccountLineQualificationSourceEnum.Ai
            && !!this._aiPartner && !!this._partner && this._aiPartner.id === this._partner.id;
    }

    public get isPartnerMemoryDetected(): boolean {
        return this._qualificationSource?.code === BankAccountLineQualificationSourceEnum.Memory
            && !!this._memoryPartner && !!this._partner && this._memoryPartner.id === this._partner.id;
    }

    public get isTypeAiDetected(): boolean {
        return this._qualificationSource?.code === BankAccountLineQualificationSourceEnum.Ai
            && !!this._aiType && !!this._type && this._aiType.code === this._type.code;
    }

    public get isTypeMemoryDetected(): boolean {
        return this._qualificationSource?.code === BankAccountLineQualificationSourceEnum.Memory
            && !!this._memoryType && !!this._type && this._memoryType.code === this._type.code;
    }

    public get justified(): boolean {
        return this._justified;
    }

    public set justified(value: any) {
        this._setValue('_justified', value, 'boolean');
    }

    public get lineHash(): string {
        return this._lineHash;
    }

    public set lineHash(value: any) {
        this._setValue('_lineHash', value, 'string');
    }

    public get loan(): Loan {
        return this._loan;
    }

    public set loan(value: any) {
        this._setValue('_loan', value, 'Employee');
    }

    public get loanInstalment(): LoanInstalment {
        return this._loanInstalment;
    }

    public set loanInstalment(value: any) {
        this._setValue('_loanInstalment', value, 'LoanInstalment');
    }

    public get loanInstalmentId(): number {
        return this._loanInstalmentId;
    }

    public set loanInstalmentId(value: any) {
        this._setValue('_loanInstalmentId', value, 'integer');
    }

    public get memoryObservations(): number | null {
        return this._memoryObservations;
    }

    public set memoryObservations(value: any) {
        this._setValue('_memoryObservations', value, 'integer');
    }

    public get memoryPartner(): Partner {
        return this._memoryPartner;
    }

    public set memoryPartner(value: any) {
        this._setValue('_memoryPartner', value, 'Partner');
    }

    public get memoryType(): BankAccountLineType {
        return this._memoryType;
    }

    public set memoryType(value: any) {
        this._setValue('_memoryType', value, 'BankAccountLineType');
    }

    public get operationDetails(): string {
        return this._operationDetails;
    }

    public set operationDetails(value: any) {
        this._setValue('_operationDetails', value, 'string');
    }

    public get operationNature(): string {
        return this._operationNature;
    }

    public set operationNature(value: any) {
        this._setValue('_operationNature', value, 'string');
    }

    public get origin(): any {
        return this._origin;
    }

    public set origin(value: any) {
        this._setValue('_origin', value, 'string');
    }

    public get paymentMethod(): PaymentMethod {
        return this._paymentMethod;
    }

    public set paymentMethod(value: any) {
        this._setValue('_paymentMethod', value, 'PaymentMethod');
    }

    public get paymentProvider(): PaymentProvider {
        return this._paymentProvider;
    }

    public set paymentProvider(value: any) {
        this._setValue('_paymentProvider', value, 'PaymentProvider');
    }

    public get qualificationSource(): BankAccountLineQualificationSource | null {
        return this._qualificationSource;
    }

    public set qualificationSource(value: any) {
        this._setValue('_qualificationSource', value, 'BankAccountLineQualificationSource');
    }

    public get valueDate(): moment.Moment {
        return this._valueDate;
    }

    public set valueDate(value: any) {
        this._setValue('_valueDate', value, 'date');
    }

}
